/* eslint-disable sonarjs/no-collapsible-if */
/* eslint-disable sonarjs/cognitive-complexity */
/**
 * @name Video
 * @description Custom HTML5 video player.
 * @class
 *
 * @example <Video />
 */

import React, { useRef, useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import cs from 'classnames';
import { PropTypes } from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import requestIdleCallback from 'ric-shim';
import LazyHydrate from '../../utils/use-lazy-hydrate';
import LoadingIndicator from '../loading-indicator';
import VideoPlayerControlOverlay from './video-player-control-overlay';
require("./video.css");
var styles = {
  "videoPlayer": "videoPlayer-2N1mv",
  "video": "video-3TKQ9",
  "button": "button-3TbWX",
  "videoWrapper": "videoWrapper-3-XLA",
  "clickToPlay": "clickToPlay-1KnyL",
  "overlay": "overlay-13EMR",
  "asPoster": "asPoster-2Z9Wj",
  "startButtonImage": "startButtonImage-1Wjdm",
  "overlayEnded": "overlayEnded-2qptI",
  "play": "play-3kjUm",
  "loadingOverlay": "loadingOverlay-13Udq",
  "controls": "controls-3E74a",
  "topControls": "topControls-53JQI",
  "show": "show-3qNBp",
  "controlsList": "controlsList-1F3H8",
  "topControlsList": "topControlsList-1xWJZ",
  "control": "control-1z5bG",
  "playNextControl": "playNextControl-1XKIG",
  "playPreviousControl": "playPreviousControl-3YCPr",
  "playNext": "playNext-JUS_k",
  "playPrevious": "playPrevious-27vUe",
  "seriesNavigationIcon": "seriesNavigationIcon-2XOZ6",
  "rangeInput": "rangeInput-3DzXV",
  "rangeBar": "rangeBar-1F6Uv",
  "volumeControl": "volumeControl-1i31b",
  "volumeButton": "volumeButton-5YdRT",
  "volumeOffIcon": "volumeOffIcon-2menz",
  "volumeRange": "volumeRange-_QCD9",
  "volumeSlider": "volumeSlider-SEQu3",
  "progressControl": "progressControl-1nzAY",
  "progressRange": "progressRange-2vFsE",
  "timeTrackerControl": "timeTrackerControl-3ZMTA",
  "timeTrackerTime": "timeTrackerTime-35OQX",
  "duration": "duration-1ExVO",
  "fullscreenControl": "fullscreenControl-13Asf",
  "toggleFullscreen": "toggleFullscreen-1rSDV",
  "closeButton": "closeButton-19KUe",
  "toggleFullscreenIcon": "toggleFullscreenIcon-UQaV6",
  "closeButtonIcon": "closeButtonIcon-1B0iB",
  "playPauseControl": "playPauseControl-2FPUZ",
  "playPause": "playPause-26L2I",
  "playIcon": "playIcon-1k8q_",
  "pauseIcon": "pauseIcon-3e5yv",
  "playPauseButton": "playPauseButton-2GjX2",
  "center": "center-3NfzY",
  "captionsControl": "captionsControl-1zJoQ",
  "toggleCaptions": "toggleCaptions-25ubC",
  "toggleCaptionsIcon": "toggleCaptionsIcon-1JXnx"
};
var VIDEO_CONTROL_TIMEOUT = 2000;
var VIDEO_TEXT_TRACK_MODES = {
  disabled: 'disabled',
  hidden: 'hidden',
  showing: 'showing'
};
var addSourceHintTag = (source, rel) => {
  var fragment = document.createDocumentFragment();
  var videoPreloader = document.createElement('link');
  videoPreloader.href = source;
  videoPreloader.rel = rel;
  videoPreloader.as = 'video';
  videoPreloader.type = 'video/mp4';
  fragment.appendChild(videoPreloader);
  document.head.appendChild(fragment);
};
var Video = /*#__PURE__*/forwardRef((_ref, ref) => {
  var {
    isAdaptiveSource,
    allowAudio,
    allowFullscreen,
    analyticsTitle,
    inline,
    autoplay,
    className,
    videoClassName,
    controlLayout,
    device,
    fullscreenSource,
    isMuted: isMutedProp,
    isPartOfSeries,
    loop,
    launchFullscreenOnStart,
    nextInSeriesButtonRef: nextInSeriesButtonRefProp,
    onEnterFullscreen,
    onExitFullscreen,
    onPlayBtnClick,
    onVideoRefInit,
    onVideoWrapperElementInit,
    playPauseButtonClassName,
    posterImageProps,
    prefetch,
    previousInSeriesButtonRef: previousInSeriesButtonRefProp,
    captionProps,
    showDuration,
    source,
    showCloseFullscreen,
    startButtonRef: startButtonRefProp,
    videoRef: videoRefProp
  } = _ref;
  var showCaptionsToggle = !!(captionProps !== null && captionProps !== void 0 && captionProps.src);
  var [isShowingCaptions, setIsShowingCaptions] = useState(true);
  var [hasBeenInView, setHasBeenInView] = useState(false);
  var [supportsFullscreen, setSupportsFullscreen] = useState(false); // fullscreen support determined during useEffect
  var [isMetaDataLoaded, setIsMetaDataLoaded] = useState(false);
  var [isFullscreen, setIsFullscreen] = useState(false);
  var [isMuted, setIsMuted] = useState(isMutedProp !== null && isMutedProp !== void 0 ? isMutedProp : !!autoplay || !allowAudio);
  var [hasBeenPlayed, setHasBeenPlayed] = useState(!!autoplay);
  var [currentTime, setCurrentTime] = useState(0);
  var [currentVolume, setCurrentVolume] = useState(1);
  var [isPlaying, setIsPlaying] = useState(false);
  var [isEnded, setIsEnded] = useState(false);
  var [isWaiting, setIsWaiting] = useState(false);
  var [showVolumeBar, setShowVolumeBar] = useState(false);
  var [showControls, setShowControls] = useState(false);
  var [videoDuration, setVideoDuration] = useState(0);
  var [videoElement, setVideoElement] = useState(null);
  var [videoWrapperElement, setVideoWrapperElement] = useState(null);
  var volumeControlRef = useRef();
  var volumeBarRef = useRef();
  var overlayRef = useRef();
  var startButtonRefLocal = useRef();
  var nextInSeriesButtonRefLocal = useRef();
  var previousInSeriesButtonRefLocal = useRef();
  var startButtonRef = startButtonRefProp || startButtonRefLocal;
  var nextInSeriesButtonRef = nextInSeriesButtonRefProp || nextInSeriesButtonRefLocal;
  var previousInSeriesButtonRef = previousInSeriesButtonRefProp || previousInSeriesButtonRefLocal;
  var [videoUID] = useState(uniqueId());
  var defaultCaptionsTrackId = "default-captions-track-".concat(videoUID);
  var timeout = useRef();
  var loadingIndicatorTimeout = useRef();

  // BEGIN - Sources preload/prefetch
  useEffect(() => {
    if (prefetch && hasBeenPlayed) {
      if (fullscreenSource && allowFullscreen) {
        requestIdleCallback(() => addSourceHintTag(fullscreenSource, 'prefetch'));
      }
    }
  }, [hasBeenPlayed, fullscreenSource, allowFullscreen, prefetch]);
  useEffect(() => {
    if (prefetch && hasBeenInView && !autoplay && source && !fullscreenSource) {
      requestIdleCallback(() => addSourceHintTag(source, 'prefetch'));
    }
  }, [source, hasBeenInView, autoplay, fullscreenSource, prefetch]);
  // END - Sources preload/prefetch

  var enterFullscreen = useCallback(() => {
    onEnterFullscreen === null || onEnterFullscreen === void 0 ? void 0 : onEnterFullscreen();
    setIsFullscreen(true);
  }, [onEnterFullscreen]);
  var exitFullscreen = useCallback(() => {
    onExitFullscreen === null || onExitFullscreen === void 0 ? void 0 : onExitFullscreen();
    setIsFullscreen(false);
  }, [onExitFullscreen]);
  useEffect(() => {
    var eventListenersToRemove = [];
    if (videoElement && videoWrapperElement && allowFullscreen && document && (document.fullscreenEnabled || document.msFullScreenEnabled || videoElement.webkitSupportsFullscreen)) {
      setSupportsFullscreen(true);
      var fullscreenChangeHandler = () => {
        var fsElem = document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
        if (fsElem !== videoWrapperElement && fsElem !== videoElement) {
          exitFullscreen();
        } else {
          enterFullscreen();
        }
      };
      videoWrapperElement.addEventListener('fullscreenchange', fullscreenChangeHandler);
      eventListenersToRemove.push({
        element: videoWrapperElement,
        type: 'fullscreenchange',
        handler: fullscreenChangeHandler
      });

      // Safari
      videoWrapperElement.addEventListener('webkitfullscreenchange', fullscreenChangeHandler);
      eventListenersToRemove.push({
        element: videoWrapperElement,
        type: 'webkitfullscreenchange',
        handler: fullscreenChangeHandler
      });

      // iOS only (no event for fullscreenchange exists for iOS)
      videoElement.addEventListener('webkitendfullscreen', exitFullscreen);
      eventListenersToRemove.push({
        element: videoElement,
        type: 'webkitfullscreenchange',
        handler: exitFullscreen
      });

      // IE11
      document.addEventListener('MSFullscreenChange', fullscreenChangeHandler);
      eventListenersToRemove.push({
        element: document,
        type: 'MSFullscreenChange',
        handler: fullscreenChangeHandler
      });
    }
    return () => {
      eventListenersToRemove.forEach(_ref2 => {
        var {
          element,
          type,
          handler
        } = _ref2;
        return element.removeEventListener(type, handler);
      });
    };
  }, [autoplay, allowFullscreen, enterFullscreen, exitFullscreen, videoElement, videoWrapperElement, isMetaDataLoaded]);
  useEffect(() => {
    if (isMetaDataLoaded) {
      setVideoDuration(videoElement.duration);
    }
  }, [isMetaDataLoaded, videoElement]);
  useEffect(() => {
    setIsMetaDataLoaded(false);
  }, [source, fullscreenSource]);

  /*
   * Triggered by captions button onClick event: update captions track mode.
   */
  var captionsToggleClickHandler = useCallback(() => {
    if (isShowingCaptions) {
      videoElement.textTracks.getTrackById(defaultCaptionsTrackId).mode = VIDEO_TEXT_TRACK_MODES.hidden;
      setIsShowingCaptions(false);
    } else {
      videoElement.textTracks.getTrackById(defaultCaptionsTrackId).mode = VIDEO_TEXT_TRACK_MODES.showing;
      setIsShowingCaptions(true);
    }
  }, [videoElement, defaultCaptionsTrackId, isShowingCaptions]);

  /*
   * Triggered by onClick event on Play button: request Fullscreen mode from browser.
   */
  var requestFullscreen = useCallback(() => {
    var promise;
    if (videoWrapperElement !== null && videoWrapperElement !== void 0 && videoWrapperElement.requestFullscreen) {
      promise = videoWrapperElement.requestFullscreen();
    } else if (videoWrapperElement !== null && videoWrapperElement !== void 0 && videoWrapperElement.webkitRequestFullscreen) {
      // Safari
      // Ensure Safari will allow playing the video
      promise = videoWrapperElement.webkitRequestFullscreen();
    } else if (videoWrapperElement !== null && videoWrapperElement !== void 0 && videoWrapperElement.msRequestFullscreen) {
      // IE11
      promise = videoWrapperElement.msRequestFullscreen();
    } else if (videoElement !== null && videoElement !== void 0 && videoElement.webkitSupportsFullscreen) {
      // iOS
      promise = videoElement.webkitEnterFullscreen();

      // Due to iOS autoplay restriction, we are not allowed to run enterFullscreen() in response to
      // webkitbeginfullscreen event since we are only allowed to play a video in response to user action.
    }

    enterFullscreen();
    return promise;
  }, [enterFullscreen, videoElement, videoWrapperElement]);
  var requestExitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitExitFullscreen) {
      // Safari + iOS
      document.webkitExitFullscreen();
    } else if (document.msExitFullscreen) {
      // IE11
      document.msExitFullscreen();
    }
    exitFullscreen();
  }, [exitFullscreen]);
  var playVideo = useCallback(() => {
    videoElement.play();
    setIsPlaying(true);
    setHasBeenPlayed(true);
    setIsEnded(false);
    onPlayBtnClick === null || onPlayBtnClick === void 0 ? void 0 : onPlayBtnClick();
  }, [videoElement, onPlayBtnClick]);
  var resetVideo = useCallback(() => {
    // reset video back to the beginning (IE11 shows black screen with anything less than .1)
    videoElement.currentTime = 0.1;
    // In IE11 the video starts again from the beginning automatically, so pause it
    videoElement.pause();
    setIsEnded(true);
    setIsPlaying(false);
    setShowControls(true);
  }, [videoElement]);
  useImperativeHandle(ref, () => ({
    requestFullscreen,
    requestExitFullscreen,
    playVideo,
    resetVideo
  }), [requestFullscreen, requestExitFullscreen, playVideo, resetVideo]);
  var resetShowControlTimeout = useCallback(() => {
    if (videoElement) {
      // if controls are not showing, show them and set the timeout
      if (!showControls) {
        setShowControls(true);
      } else {
        // if controls are showing, reset the timeout to keep showing
        clearTimeout(timeout.current);
      }

      // Hide the controls after 3 seconds
      timeout.current = setTimeout(() => {
        // Only alter state if the video player is still mounted
        if (videoElement) {
          setShowControls(false);
        }
      }, VIDEO_CONTROL_TIMEOUT);
    }
  }, [showControls, videoElement]);

  /*
   * Triggered by onWaiting event: video is in waiting (loading) state.
   */
  var showLoadingState = useCallback(() => {
    if (!autoplay) {
      loadingIndicatorTimeout.current = setTimeout(() => {
        // Only alter state if the video player is still mounted
        if (videoElement) {
          // Show loading indicator if video is waiting for more than 300ms
          setIsWaiting(true);
        }
      }, 300);
    }
  }, [videoElement, autoplay]);
  var resetLoadingState = useCallback(() => {
    if (!autoplay) {
      clearTimeout(loadingIndicatorTimeout.current);
      if (videoElement) {
        setIsWaiting(false);
      }
    }
  }, [videoElement, autoplay]);
  var fullscreenButtonClickHandler = useCallback(() => {
    if (isFullscreen) {
      requestExitFullscreen();
    } else {
      requestFullscreen();
    }
  }, [isFullscreen, requestExitFullscreen, requestFullscreen]);

  /*
   * Triggered by onended event: video has ended.
   */
  var endOfVideoHandler = useCallback(() => {
    resetVideo();
  }, [resetVideo]);
  var progressRangeClickHandler = useCallback(clickOffsetPercent => {
    // update video's current time based on click on progress bar
    videoElement.currentTime = videoElement.duration * clickOffsetPercent;
  }, [videoElement]);
  var progressKeydownHandler = useCallback(e => {
    e.stopPropagation();
    if (e.key === 'ArrowRight') {
      // jump forward by 5 seconds
      videoElement.currentTime += 5;
    } else if (e.key === 'ArrowLeft') {
      // jump back by 5 seconds
      videoElement.currentTime -= 5;
    }
  }, [videoElement]);
  var playPauseButtonClickHandler = useCallback(() => {
    if (!hasBeenPlayed && launchFullscreenOnStart && !isFullscreen) {
      requestFullscreen();
    }
    if (videoElement.paused || videoElement.ended) {
      playVideo();
    } else {
      videoElement.pause();
      setIsPlaying(false);
    }
    resetShowControlTimeout();
  }, [videoElement, hasBeenPlayed, isFullscreen, launchFullscreenOnStart, playVideo, requestFullscreen, resetShowControlTimeout]);

  /*
   * To handle the case where on Android devices, the first tap on the video should show controls but not play/pause video.
   * Clicking when controls are already showing should play/pause the video
   */
  var overlayClickHandler = useCallback(e => {
    e.stopPropagation();
    if ((device === 'desktop' || showControls) && controlLayout !== 'minimal' && e.target === overlayRef.current) {
      playPauseButtonClickHandler(e);
    }
    resetShowControlTimeout(e);
  }, [controlLayout, device, overlayRef, playPauseButtonClickHandler, resetShowControlTimeout, showControls]);

  /*
   * Triggered by volume input range onChange event: update video volume property.
   */
  var volumeInputChangeHandler = useCallback(e => {
    e.stopPropagation();
    setCurrentVolume(+volumeBarRef.current.value);
    videoElement.volume = volumeBarRef.current.value;
    if (isMuted && videoElement.volume > 0) {
      videoElement.muted = false;
      setIsMuted(false);
    }
  }, [isMuted, videoElement, volumeBarRef]);

  /*
   * Triggered by volume input range onClick event: update value of volume input range to match click location.
   */
  var volumeRangeClickHandler = useCallback(clickOffsetPercent => {
    volumeBarRef.current.value = clickOffsetPercent;
    setCurrentVolume(+volumeBarRef.current.value);
    videoElement.volume = volumeBarRef.current.value;
    if (isMuted && videoElement.volume > 0) {
      videoElement.muted = false;
      setIsMuted(false);
    }
  }, [isMuted, videoElement, volumeBarRef]);

  /*
   * Triggered by mute button onClick event: update video muted property.
   */
  var muteButtonClickHandler = useCallback(e => {
    e.stopPropagation();
    // reset volume to the middle on unmute if its already at 0
    if (isMuted) {
      if (controlLayout === 'minimal') {
        setCurrentVolume(1);
        videoElement.volume = 1;
      } else {
        setCurrentVolume(0.5);
        volumeBarRef.current.value = 0.5;
        videoElement.volume = 0.5;
      }
    } else {
      setCurrentVolume(0);
    }
    videoElement.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [controlLayout, isMuted, videoElement, volumeBarRef]);
  var volumeBarHoverChangeHandler = useCallback(isHover => {
    // controls visibility of volumeBar on hover & focus
    setShowVolumeBar(isHover);

    // always display volumeBar as long as its children have focus
    if (volumeControlRef.current && volumeControlRef.current.contains(document.activeElement)) {
      setShowVolumeBar(true);
    }
  }, [volumeControlRef]);

  /*
   * Keep controls visible while video buttons have keyboard focus
   */
  var buttonFocusHandler = useCallback(() => {
    clearTimeout(timeout.current);
    if (!showControls) {
      setShowControls(true);
    }

    // show volumeBar when it has focus and hide when it does not
    if (volumeControlRef.current) {
      setShowVolumeBar(volumeControlRef.current.contains(document.activeElement));
    }
  }, [showControls, volumeControlRef]);

  /*
   * Reset controls timer when controls lose keyboard focus
   */
  var buttonBlurHandler = useCallback(() => {
    resetShowControlTimeout();
  }, [resetShowControlTimeout]);
  var timeUpdateHandler = useCallback(() => {
    setCurrentTime(videoElement.currentTime);
  }, [videoElement]);
  var metaDataLoadedHandler = useCallback(() => {
    setIsMetaDataLoaded(true);
    videoElement.muted = isMuted;
  }, [isMuted, videoElement]);
  var onPlayHandler = useCallback(() => {
    setIsPlaying(true);
  }, []);
  var setVideoRef = useCallback(el => {
    setVideoElement(el);
    if (onVideoRefInit) onVideoRefInit(el);
    if (videoRefProp) {
      if (typeof videoRefProp === 'function') {
        videoRefProp(el);
      } else {
        // eslint-disable-next-line no-param-reassign
        videoRefProp.current = el;
      }
    }
  }, [onVideoRefInit, videoRefProp]);
  var setVideoWrapperRef = useCallback(el => setVideoWrapperElement(el), []);
  useEffect(() => {
    if (videoWrapperElement && onVideoWrapperElementInit) {
      onVideoWrapperElementInit(videoWrapperElement);
    }
  }, [videoWrapperElement, onVideoWrapperElementInit]);
  var videoSource = isFullscreen && fullscreenSource ? fullscreenSource : source;
  return /*#__PURE__*/React.createElement(LazyHydrate, {
    style: null,
    className: cs(styles.videoPlayer, (!hasBeenPlayed || isEnded) && styles.clickToPlay, className),
    whenVisible: true,
    didHydrate: () => setHasBeenInView(true)
  }, /*#__PURE__*/React.createElement("div", {
    ref: setVideoWrapperRef,
    className: styles.videoWrapper,
    onMouseMove: e => {
      if (device === 'desktop') {
        resetShowControlTimeout(e);
      }
    }
  }, /*#__PURE__*/React.createElement("video", {
    autoPlay: autoplay,
    className: cs(styles.video, videoClassName),
    "data-testid": "video",
    key: "".concat(fullscreenSource).concat(source).concat(videoUID),
    loop: loop,
    muted: isMuted,
    onCanPlay: resetLoadingState,
    onEnded: endOfVideoHandler,
    onLoadedMetadata: metaDataLoadedHandler,
    onPause: resetLoadingState,
    onPlay: onPlayHandler,
    onTimeUpdate: timeUpdateHandler,
    onWaiting: showLoadingState,
    playsInline: autoplay || inline,
    poster: posterImageProps === null || posterImageProps === void 0 ? void 0 : posterImageProps.src,
    preload: "metadata",
    ref: setVideoRef,
    src: isAdaptiveSource ? videoSource : undefined,
    title: analyticsTitle || '',
    crossOrigin: "anonymous"
  }, !isAdaptiveSource && /*#__PURE__*/React.createElement("source", {
    key: "video-source-".concat(videoUID),
    src: videoSource,
    type: "video/mp4"
  }), (captionProps === null || captionProps === void 0 ? void 0 : captionProps.src) && /*#__PURE__*/React.createElement("track", {
    default: true,
    id: defaultCaptionsTrackId,
    kind: "captions",
    label: captionProps === null || captionProps === void 0 ? void 0 : captionProps.label,
    src: captionProps === null || captionProps === void 0 ? void 0 : captionProps.src,
    srcLang: captionProps === null || captionProps === void 0 ? void 0 : captionProps.srcLang
  })), isWaiting && /*#__PURE__*/React.createElement("div", {
    className: styles.loadingOverlay
  }, /*#__PURE__*/React.createElement(LoadingIndicator, {
    color: "white"
  })), /*#__PURE__*/React.createElement(VideoPlayerControlOverlay, {
    controlLayout: controlLayout,
    currentTime: currentTime,
    currentVolume: currentVolume,
    hasBeenPlayed: hasBeenPlayed,
    isEnded: isEnded,
    isFullscreen: isFullscreen,
    isMuted: isMuted,
    isPartOfSeries: isPartOfSeries,
    isPlaying: isPlaying,
    isShowingCaptions: isShowingCaptions,
    launchFullscreenOnStart: launchFullscreenOnStart,
    nextInSeriesButtonRef: nextInSeriesButtonRef,
    onButtonFocus: buttonFocusHandler,
    onButtonBlur: buttonBlurHandler,
    onCaptionsToggleClick: captionsToggleClickHandler,
    onFullscreenButtonClick: fullscreenButtonClickHandler,
    onMuteButtonClick: muteButtonClickHandler,
    onOverlayClick: overlayClickHandler,
    onPlayPauseButtonClick: playPauseButtonClickHandler,
    onProgressKeyDown: progressKeydownHandler,
    onProgressRangeClick: progressRangeClickHandler,
    onVolumeBarHoverChange: volumeBarHoverChangeHandler,
    onVolumeInputChange: volumeInputChangeHandler,
    onVolumeRangeClick: volumeRangeClickHandler,
    overlayRef: overlayRef,
    playPauseButtonClassName: playPauseButtonClassName,
    previousInSeriesButtonRef: previousInSeriesButtonRef,
    showCaptionsToggle: showCaptionsToggle,
    showCloseFullscreen: showCloseFullscreen,
    showControls: showControls,
    showDuration: showDuration,
    showFullscreenToggle: allowFullscreen && supportsFullscreen,
    showVolumeBar: showVolumeBar,
    startButtonImageProps: posterImageProps,
    startButtonRef: startButtonRef,
    supportsAudio: allowAudio,
    videoDuration: videoDuration,
    videoUID: videoUID,
    volumeBarRef: volumeBarRef,
    volumeControlRef: volumeControlRef
  })));
});
Video.propTypes = {
  allowAudio: PropTypes.bool,
  allowFullscreen: PropTypes.bool,
  analyticsTitle: PropTypes.string,
  inline: PropTypes.bool,
  autoplay: PropTypes.bool,
  className: PropTypes.string,
  videoClassName: PropTypes.string,
  controlLayout: PropTypes.oneOf(['minimal', 'inline', 'stacked']),
  device: PropTypes.string,
  fullscreenSource: PropTypes.string,
  isAdaptiveSource: PropTypes.bool,
  isMuted: PropTypes.bool,
  isPartOfSeries: PropTypes.bool,
  loop: PropTypes.bool,
  launchFullscreenOnStart: PropTypes.bool,
  nextInSeriesButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  onEnterFullscreen: PropTypes.func,
  onExitFullscreen: PropTypes.func,
  onPlayBtnClick: PropTypes.func,
  onVideoRefInit: PropTypes.func,
  onVideoWrapperElementInit: PropTypes.func,
  playPauseButtonClassName: PropTypes.string,
  posterImageProps: PropTypes.shape({
    alt: PropTypes.string,
    src: PropTypes.string.isRequired
  }),
  prefetch: PropTypes.bool,
  previousInSeriesButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  captionProps: PropTypes.shape({
    label: PropTypes.string,
    src: PropTypes.string.isRequired,
    srcLang: PropTypes.string
  }),
  showCloseFullscreen: PropTypes.bool,
  showDuration: PropTypes.bool,
  source: PropTypes.string.isRequired,
  startButtonRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  videoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object])
};
Video.defaultProps = {
  allowAudio: true,
  allowFullscreen: true,
  analyticsTitle: null,
  inline: true,
  autoplay: false,
  className: null,
  controlLayout: 'inline',
  device: 'desktop',
  fullscreenSource: null,
  isAdaptiveSource: false,
  isMuted: null,
  isPartOfSeries: false,
  loop: false,
  launchFullscreenOnStart: false,
  nextInSeriesButtonRef: null,
  onEnterFullscreen: null,
  onExitFullscreen: null,
  onPlayBtnClick: null,
  onVideoRefInit: null,
  onVideoWrapperElementInit: null,
  playPauseButtonClassName: null,
  posterImageProps: null,
  prefetch: true,
  previousInSeriesButtonRef: null,
  captionProps: null,
  showCloseFullscreen: false,
  showDuration: true,
  startButtonRef: null,
  videoClassName: null,
  videoRef: null
};
export default Video;